import React, { forwardRef, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Segmented, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { DriverActivityRuleTemplate } from '../graphql/types';
import {
    RuleTextDraft,
    insertVariableAtCursor,
    removeVariableAtCursor,
    ruleTextLength,
    tokenizeRuleText,
    variableLabel,
    variablePlaceholder,
} from '../utils/ruleTextUtils';

interface RuleTextEditorProps {
    value?: RuleTextDraft;
    onChange?: (draft: RuleTextDraft) => void;
    template: DriverActivityRuleTemplate;
    disabled?: boolean;
    /** 卡头右侧额外按钮（预览） */
    extra?: React.ReactNode;
    /** antd Form.Item 注入的字段 id，供 scrollToField 定位 */
    id?: string;
}

/** 输入法组合中的 keydown 固定 keyCode（isComposing 之外的兜底） */
const IME_COMPOSING_KEY_CODE = 229;

/** 零宽空格：放在 chip 前后保证光标能停在 chip 两侧，序列化时剔除 */
const ZWSP = '​';
const VARIABLE_ATTR = 'data-variable';

// ---------- contenteditable ↔ 底层文本（{{key}}）互转 ----------

function stripZwsp(text: string): string {
    return text.split(ZWSP).join('');
}

function isChip(node: Node): boolean {
    return node.nodeType === Node.ELEMENT_NODE && (node as HTMLElement).hasAttribute(VARIABLE_ATTR);
}

function isFillerBr(root: HTMLElement, node: Node): boolean {
    return node.nodeType === Node.ELEMENT_NODE && (node as HTMLElement).tagName === 'BR' && node === root.lastChild;
}

/** 单个节点对应的底层文本：chip → {{key}}，<br> → 换行（末尾填充 br 除外），块元素前补换行 */
function serializeNode(root: HTMLElement, node: Node): string {
    if (node.nodeType === Node.TEXT_NODE) {
        return stripZwsp(node.textContent ?? '');
    }
    if (node.nodeType !== Node.ELEMENT_NODE) {
        return '';
    }
    const el = node as HTMLElement;
    if (isChip(el)) {
        return variablePlaceholder(el.getAttribute(VARIABLE_ATTR) ?? '');
    }
    if (el.tagName === 'BR') {
        return isFillerBr(root, el) ? '' : '\n';
    }
    let text = '';
    el.childNodes.forEach((child) => {
        text += serializeNode(root, child);
    });
    if (el.tagName === 'DIV' || el.tagName === 'P') {
        text = `\n${text}`;
    }
    return text;
}

/** 编辑区 DOM → 底层文本 */
export function serializeEditor(root: HTMLElement): string {
    let text = '';
    root.childNodes.forEach((child) => {
        text += serializeNode(root, child);
    });
    return text;
}

/** 底层文本 → 编辑区 DOM：普通文本用文本节点，变量用不可编辑的 chip，chip 两侧保证有文本节点 */
function renderEditor(root: HTMLElement, text: string, labelOf: (key: string) => string, disabled: boolean) {
    const doc = root.ownerDocument;
    root.innerHTML = '';
    const tokens = tokenizeRuleText(text);
    let lastWasChip = true;
    tokens.forEach((token) => {
        if (token.type === 'variable') {
            if (lastWasChip) root.appendChild(doc.createTextNode(ZWSP));
            const chip = doc.createElement('span');
            chip.setAttribute(VARIABLE_ATTR, token.value);
            chip.setAttribute('contenteditable', 'false');
            chip.setAttribute('data-testid', `editor-chip-${token.value}`);
            chip.className = `inline-block bg-[#F3EEFE] text-[#722ed1] rounded px-[6px] text-[12.5px] leading-5 mx-[2px] select-none ${
                disabled ? 'opacity-60' : ''
            }`.trim();
            chip.textContent = labelOf(token.value);
            root.appendChild(chip);
            lastWasChip = true;
        } else {
            root.appendChild(doc.createTextNode(token.value));
            lastWasChip = false;
        }
    });
    if (lastWasChip && tokens.length) root.appendChild(doc.createTextNode(ZWSP));
    if (text.endsWith('\n')) root.appendChild(doc.createElement('br'));
}

/** (node, offset) → 底层文本偏移 */
export function caretOffsetOf(root: HTMLElement, node: Node, offset: number): number {
    let total = 0;
    const walk = (current: Node): boolean => {
        if (current === node) {
            if (current.nodeType === Node.TEXT_NODE) {
                total += stripZwsp((current.textContent ?? '').slice(0, offset)).length;
            } else {
                const children = Array.from(current.childNodes).slice(0, offset);
                children.forEach((child) => {
                    total += serializeNode(root, child).length;
                });
            }
            return true;
        }
        if (isChip(current)) {
            if (current.contains(node)) {
                total += serializeNode(root, current).length;
                return true;
            }
            total += serializeNode(root, current).length;
            return false;
        }
        if (current.nodeType !== Node.ELEMENT_NODE) {
            total += serializeNode(root, current).length;
            return false;
        }
        const el = current as HTMLElement;
        if (el.tagName === 'BR') {
            total += serializeNode(root, el).length;
            return false;
        }
        if (el !== root && (el.tagName === 'DIV' || el.tagName === 'P')) total += 1;
        for (let i = 0; i < el.childNodes.length; i += 1) {
            if (walk(el.childNodes[i])) return true;
        }
        return false;
    };
    walk(root);
    return total;
}

/** 把光标放到底层文本偏移处；落在 chip 内部时放到 chip 前面 */
export function placeCaret(root: HTMLElement, offset: number) {
    const doc = root.ownerDocument;
    const selection = doc.defaultView?.getSelection();
    if (!selection) return;
    const range = doc.createRange();
    let remaining = offset;
    const walk = (current: Node): boolean => {
        if (current.nodeType === Node.TEXT_NODE) {
            const content = current.textContent ?? '';
            let logical = 0;
            for (let i = 0; i < content.length; i += 1) {
                if (logical === remaining) {
                    range.setStart(current, i);
                    return true;
                }
                if (content[i] !== ZWSP) logical += 1;
            }
            if (logical === remaining) {
                range.setStart(current, content.length);
                return true;
            }
            remaining -= logical;
            return false;
        }
        if (isChip(current)) {
            const length = serializeNode(root, current).length;
            if (remaining < length) {
                range.setStartBefore(current);
                return true;
            }
            remaining -= length;
            return false;
        }
        if (current.nodeType !== Node.ELEMENT_NODE) return false;
        const el = current as HTMLElement;
        if (el.tagName === 'BR') {
            const length = serializeNode(root, el).length;
            if (remaining < length) {
                range.setStartBefore(el);
                return true;
            }
            remaining -= length;
            return false;
        }
        for (let i = 0; i < el.childNodes.length; i += 1) {
            if (walk(el.childNodes[i])) return true;
        }
        return false;
    };
    if (!walk(root)) {
        range.selectNodeContents(root);
        range.collapse(false);
    }
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);
}

// ---------- 单段编辑器 ----------

export interface SectionEditorHandle {
    /** 聚焦并把光标放到底层文本偏移处（插入变量后调用） */
    focusAt: (offset: number) => void;
}

interface SectionEditorProps {
    value: string;
    onChange: (text: string) => void;
    disabled?: boolean;
    error?: boolean;
    placeholder?: string;
    labelOf: (key: string) => string;
    onSelectionChange: (start: number, end: number) => void;
    testId: string;
}

/**
 * contenteditable 编辑区：变量渲染成不可编辑的紫底 chip（显示名），底层值仍是 {{key}}。
 * Enter / 粘贴 / 变量插入都走底层文本拼接再重绘；普通打字由浏览器处理后反序列化回底层文本。
 */
const SectionEditor = forwardRef<SectionEditorHandle, SectionEditorProps>(
    ({ value, onChange, disabled, error, placeholder, labelOf, onSelectionChange, testId }, ref) => {
        const rootRef = useRef<HTMLDivElement>(null);
        const pendingCaret = useRef<number | null>(null);

        useLayoutEffect(() => {
            const root = rootRef.current;
            if (!root) return;
            if (serializeEditor(root) !== value) {
                renderEditor(root, value, labelOf, !!disabled);
            }
            if (pendingCaret.current !== null) {
                placeCaret(root, pendingCaret.current);
                pendingCaret.current = null;
            }
        });

        // 置灰切换时 chip 样式变化，强制重绘（不依赖 labelOf：它随 t 变化会打断正在输入的光标）
        useEffect(() => {
            const root = rootRef.current;
            if (root) renderEditor(root, value, labelOf, !!disabled);
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [disabled]);

        const currentSelection = (): { start: number; end: number } | null => {
            const root = rootRef.current;
            const selection = root?.ownerDocument.defaultView?.getSelection();
            if (!root || !selection || selection.rangeCount === 0) return null;
            const range = selection.getRangeAt(0);
            if (!root.contains(range.startContainer) || !root.contains(range.endContainer)) return null;
            const start = caretOffsetOf(root, range.startContainer, range.startOffset);
            const end = caretOffsetOf(root, range.endContainer, range.endOffset);
            return { start: Math.min(start, end), end: Math.max(start, end) };
        };

        const captureSelection = () => {
            const sel = currentSelection();
            if (sel) onSelectionChange(sel.start, sel.end);
        };

        const replaceSelection = (insert: string) => {
            const sel = currentSelection() ?? { start: value.length, end: value.length };
            const next = value.slice(0, sel.start) + insert + value.slice(sel.end);
            pendingCaret.current = sel.start + insert.length;
            onChange(next);
            onSelectionChange(pendingCaret.current, pendingCaret.current);
        };

        useImperativeHandle(ref, () => ({
            // 只记下目标偏移并聚焦；真正落光标交给 layoutEffect（插入变量一定伴随重绘，
            // 在这里同步 placeCaret 会落在重绘前的旧 DOM 上，随后被 innerHTML 重写冲掉）
            focusAt: (offset: number) => {
                pendingCaret.current = offset;
                rootRef.current?.focus();
            },
        }));

        const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
            if (disabled) return;
            // 中/日文输入法选词阶段的 Enter / Backspace 交给输入法，否则会打断候选并多插一个换行
            if (e.nativeEvent.isComposing || e.keyCode === IME_COMPOSING_KEY_CODE) return;
            if (e.key === 'Enter') {
                e.preventDefault();
                replaceSelection('\n');
                return;
            }
            if (e.key === 'Backspace' || e.key === 'Delete') {
                const sel = currentSelection();
                if (!sel || sel.start !== sel.end) return;
                const result = removeVariableAtCursor(value, sel.start, e.key === 'Backspace' ? 'backward' : 'forward');
                if (result) {
                    e.preventDefault();
                    pendingCaret.current = result.cursor;
                    onChange(result.text);
                    onSelectionChange(result.cursor, result.cursor);
                }
            }
        };

        const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
            e.preventDefault();
            if (disabled) return;
            replaceSelection(e.clipboardData.getData('text/plain'));
        };

        const handleInput = () => {
            const root = rootRef.current;
            if (!root) return;
            const next = serializeEditor(root);
            if (next !== value) onChange(next);
            captureSelection();
        };

        return (
            <div className="relative">
                {!value && placeholder && (
                    <span className="absolute left-[11px] top-[6px] text-[13.5px] leading-[1.8] text-[#BFBFBF] pointer-events-none">{placeholder}</span>
                )}
                <div
                    ref={rootRef}
                    role="textbox"
                    aria-multiline="true"
                    aria-disabled={disabled}
                    tabIndex={disabled ? -1 : 0}
                    contentEditable={!disabled}
                    suppressContentEditableWarning
                    data-testid={testId}
                    className={`rounded-[6px] border px-[11px] py-[6px] text-[13.5px] leading-[1.8] whitespace-pre-wrap break-words outline-none min-h-[62px] ${
                        disabled ? 'bg-[#F5F5F5] text-[#BFBFBF] cursor-not-allowed' : 'bg-white text-[#1F1F1F]'
                    } ${error ? 'border-[#F0453A]' : 'border-[#D9D9D9] focus:border-[#722ed1]'}`}
                    onInput={handleInput}
                    onKeyDown={handleKeyDown}
                    onKeyUp={captureSelection}
                    onMouseUp={captureSelection}
                    onClick={captureSelection}
                    onFocus={captureSelection}
                    onPaste={handlePaste}
                    onDrop={(e) => e.preventDefault()}
                />
            </div>
        );
    }
);
SectionEditor.displayName = 'SectionEditor';

// ---------- 六段编辑器 ----------

/**
 * 规则文案：中文 / English / Español 分段切换；六段固定表左标题右正文；
 * 变量 chip 点击在光标处插入 {{key}}（编辑区显示变量名）；每段 ≤ sectionMaxLength 计数；必填段带红星。
 */
const RuleTextEditor: React.FC<RuleTextEditorProps> = ({ value = {}, onChange, template, disabled, extra, id }) => {
    const { t, i18n } = useTranslation();
    const [lang, setLang] = useState(template.languages[0]);
    const [focused, setFocused] = useState<{ section: string; start: number; end: number } | null>(null);
    const refs = useRef<Record<string, React.RefObject<SectionEditorHandle>>>({});
    // 段标题 / 变量 chip / 占位提示跟当前编辑的文案语种走，英文文案里不混中文 chip；语种 tab 和「插入变量」标签仍用界面语言
    const tl = useMemo(() => i18n.getFixedT(lang), [i18n, lang]);
    const labelOf = React.useCallback((key: string) => variableLabel(tl, key), [tl]);

    useEffect(() => {
        if (!template.languages.includes(lang)) setLang(template.languages[0]);
    }, [template.languages, lang]);

    // 切语种后光标记录失效
    useEffect(() => {
        setFocused(null);
    }, [lang]);

    const getRef = (key: string) => {
        if (!refs.current[key]) refs.current[key] = React.createRef<SectionEditorHandle>();
        return refs.current[key];
    };

    const setSection = (sectionKey: string, text: string) => {
        onChange?.({ ...value, [lang]: { ...(value[lang] ?? {}), [sectionKey]: text } });
    };

    const insertVariable = (key: string) => {
        if (disabled) return;
        if (!focused) {
            message.info(t('driverOps.activity.form.selectSectionFirst'));
            return;
        }
        const current = value[lang]?.[focused.section] ?? '';
        const result = insertVariableAtCursor(current, focused.start, focused.end, key);
        setSection(focused.section, result.text);
        setFocused({ section: focused.section, start: result.cursor, end: result.cursor });
        getRef(focused.section).current?.focusAt(result.cursor);
    };

    const langOptions = template.languages.map((code) => ({
        value: code,
        label: t(`driverOps.activity.languages.${code}`, { defaultValue: code }),
    }));

    return (
        <div className="w-full min-w-0" id={id}>
            <div className="flex items-center gap-3 mb-[10px]">
                <Segmented size="small" options={langOptions} value={lang} onChange={(v) => setLang(String(v))} />
                <span className="ml-auto">{extra}</span>
            </div>
            <div className="flex items-center gap-[6px] mb-[10px] flex-wrap">
                <span className="text-[12.5px] text-[#8C8C8C]">{t('driverOps.activity.form.insertVariable')}</span>
                {template.variables.map((key) => (
                    <span
                        key={key}
                        role="button"
                        data-testid={`variable-chip-${key}`}
                        className={`inline-block bg-[#F3EEFE] text-[#722ed1] rounded px-[6px] text-[12.5px] leading-5 ${
                            disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'
                        }`}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => insertVariable(key)}
                    >
                        {labelOf(key)}
                    </span>
                ))}
            </div>
            <div className="border border-[#D9D9D9] rounded-[6px] overflow-hidden w-full">
                {template.sections.map((section, index) => {
                    const text = value[lang]?.[section.key] ?? '';
                    const length = ruleTextLength(text);
                    const over = length > template.sectionMaxLength;
                    return (
                        <div key={section.key} className={`flex ${index === template.sections.length - 1 ? '' : 'border-b border-[#EFEFEF]'}`}>
                            <div className="w-[132px] shrink-0 bg-[#FAFAFA] px-3 py-[10px] font-semibold text-[13.5px] text-[#1F1F1F] border-r border-[#EFEFEF] break-words">
                                {section.required && <span className="text-[#F0453A] mr-[3px]">*</span>}
                                {tl(`driverOps.activity.sections.${section.key}`, { defaultValue: section.key })}
                            </div>
                            <div className="flex-1 min-w-0 p-2">
                                <SectionEditor
                                    key={lang}
                                    ref={getRef(section.key)}
                                    value={text}
                                    disabled={disabled}
                                    error={over}
                                    placeholder={tl(`driverOps.activity.sectionPlaceholders.${section.key}`, { defaultValue: '' }) as string}
                                    labelOf={labelOf}
                                    onChange={(next) => setSection(section.key, next)}
                                    onSelectionChange={(start, end) => setFocused({ section: section.key, start, end })}
                                    testId={`section-editor-${section.key}`}
                                />
                                <div
                                    className={`text-right text-[12px] mt-1 ${over ? 'text-[#F0453A]' : 'text-[#8C8C8C]'}`}
                                    data-testid={`section-count-${section.key}`}
                                >
                                    {length} / {template.sectionMaxLength}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default RuleTextEditor;
